import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Modal,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import Swal from 'sweetalert2';
import Page from '../../componentes/Page';
import Iconify from '../../componentes/Iconify';
import CONFIG from '../../CONFIG.json';
import { buildBody, workWithLocalStorage } from '../../auxiliares/funciones';

const getApiBaseUrl = () => CONFIG.api || '/api/';

const extraerCodigo = (raw) => {
  if (!raw) return '';
  let str = String(raw).trim();
  if (str.includes('/')) {
    const parts = str.split('/').filter(Boolean);
    str = parts[parts.length - 1] || str;
  }
  if (str.includes('?')) {
    str = str.split('?')[0];
  }
  return str.trim();
};

const getEstadoLabel = (ticket) => {
  if (!ticket) return 'DESCONOCIDO';
  if (ticket.estado_uso === 'USADO') return 'USADO';
  if (ticket.caducado) return 'CADUCADO';
  return 'VÁLIDO / NO USADO';
};

const getEstadoColor = (ticket) => {
  if (!ticket) return 'default';
  if (ticket.estado_uso === 'USADO') return 'default';
  if (ticket.caducado) return 'error';
  return 'success';
};

// Carga asíncrona del script html5-qrcode para evitar advertencias de compilación Webpack
const loadHtml5QrcodeClass = async () => {
  if (typeof window !== 'undefined' && window.Html5Qrcode) {
    return window.Html5Qrcode;
  }
  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById('html5-qrcode-cdn-script');
    if (existingScript) {
      if (window.Html5Qrcode) {
        resolve(window.Html5Qrcode);
      } else {
        existingScript.addEventListener('load', () => resolve(window.Html5Qrcode));
        existingScript.addEventListener('error', (e) => reject(e));
      }
      return;
    }
    const script = document.createElement('script');
    script.id = 'html5-qrcode-cdn-script';
    script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
    script.onload = () => resolve(window.Html5Qrcode);
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });
};

export default function LectorCupones(props) {
  const usuario = workWithLocalStorage('get', 'usuario', null);

  const [cameraActive, setCameraActive] = useState(false);
  const [codigoInput, setCodigoInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Modal 1: Detalle del Ticket
  const [modalDetalleOpen, setModalDetalleOpen] = useState(false);
  const [ticketData, setTicketData] = useState(null);

  // Modal 2: Lista de Citas +/- 3 Horas
  const [modalCitasOpen, setModalCitasOpen] = useState(false);
  const [citasRango, setCitasRango] = useState([]);
  const [loadingCitas, setLoadingCitas] = useState(false);

  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Ocultar el loader global del layout al montar el componente
  useEffect(() => {
    if (props && typeof props.showLoader === 'function') {
      props.showLoader(false);
    }
  }, [props]);

  // Inicializar o destruir el escáner de cámara
  useEffect(() => {
    let isMounted = true;

    if (cameraActive) {
      const loadScanner = async () => {
        try {
          const Html5Qrcode = await loadHtml5QrcodeClass();

          if (!isMounted) return;

          if (!html5QrCodeRef.current && Html5Qrcode) {
            html5QrCodeRef.current = new Html5Qrcode('qr-reader-container');
          }

          const qrCodeSuccessCallback = (decodedText) => {
            if (decodedText) {
              if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
                html5QrCodeRef.current.stop().catch(() => {});
              }
              setCameraActive(false);
              const codigoLimpio = extraerCodigo(decodedText);
              setCodigoInput(codigoLimpio);
              validarCodigo(codigoLimpio);
            }
          };

          const config = { fps: 10, qrbox: { width: 250, height: 250 } };

          if (html5QrCodeRef.current) {
            await html5QrCodeRef.current.start(
              { facingMode: 'environment' },
              config,
              qrCodeSuccessCallback,
              () => {}
            );
          }
        } catch (err) {
          console.error('Error al iniciar cámara:', err);
          if (isMounted) {
            Swal.fire({
              icon: 'error',
              title: 'Error de Cámara',
              text: 'No se pudo acceder a la cámara. Asegúrate de otorgar permisos o introduce el código manualmente.',
              confirmButtonColor: '#10B981'
            });
            setCameraActive(false);
          }
        }
      };

      loadScanner();
    } else if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
      html5QrCodeRef.current.stop().then(() => {
        html5QrCodeRef.current.clear();
      }).catch((err) => console.error('Error al detener cámara:', err));
    }

    return () => {
      isMounted = false;
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraActive]);

  // Validar código manual o escaneado vía Proxy Backend
  const validarCodigo = async (codigoOpcional) => {
    const rawCode = (codigoOpcional || codigoInput || '').trim();
    const codeToValidate = extraerCodigo(rawCode);

    if (!codeToValidate) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Por favor, introduce o escanea un código de cupón válido.',
        confirmButtonColor: '#10B981'
      });
      return;
    }

    setCodigoInput(codeToValidate);

    setLoading(true);
    if (props && typeof props.showLoader === 'function') props.showLoader(true);

    try {
      const baseUrl = getApiBaseUrl();
      const userToken = usuario ? usuario.token : null;
      const reqBody = buildBody(userToken, { uuid: codeToValidate, codigo: codeToValidate });

      const response = await fetch(`${baseUrl}cupones-externos/validar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reqBody)
      });

      const result = await response.json();
      setLoading(false);
      if (props && typeof props.showLoader === 'function') props.showLoader(false);

      if (result.error === 0 && result.data) {
        setTicketData(result.data);
        setModalDetalleOpen(true);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Cupón no Válido',
          text: result.errorMessage || 'No se pudo validar el cupón o no existe.',
          confirmButtonColor: '#10B981'
        });
      }
    } catch (error) {
      setLoading(false);
      if (props && typeof props.showLoader === 'function') props.showLoader(false);
      console.error('Error al validar cupón:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error de Conexión',
        text: 'Ocurrió un error al conectar con el servidor para validar el cupón.',
        confirmButtonColor: '#10B981'
      });
    }
  };

  // Abrir Modal 2 (Buscar Citas +/- 3h)
  const handleAbrirCitas = async () => {
    setModalCitasOpen(true);
    setLoadingCitas(true);

    try {
      const baseUrl = getApiBaseUrl();
      const userToken = usuario ? usuario.token : null;
      const reqBody = buildBody(userToken, {});

      const response = await fetch(`${baseUrl}cupones-externos/citas-rango`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reqBody)
      });

      const result = await response.json();
      setLoadingCitas(false);

      if (result.error === 0) {
        setCitasRango(result.data || []);
      } else {
        setCitasRango([]);
      }
    } catch (error) {
      setLoadingCitas(false);
      console.error('Error al cargar citas:', error);
      setCitasRango([]);
    }
  };

  // Canjear Cupón y Asociar a Cita Seleccionada
  const handleCanjearYCitar = async (citaId) => {
    if (!ticketData || !ticketData.uuid) return;

    const confirmRes = await Swal.fire({
      title: '¿Confirmar Canje?',
      text: `Se marcará el cupón "${ticketData.uuid}" como USADO y se anotará en la cita seleccionada.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, Canjear y Asociar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10B981',
      cancelButtonColor: '#64748B'
    });

    if (!confirmRes.isConfirmed) return;

    setLoading(true);
    if (props && typeof props.showLoader === 'function') props.showLoader(true);

    try {
      const baseUrl = getApiBaseUrl();
      const userToken = usuario ? usuario.token : null;
      const reqBody = buildBody(userToken, {
        uuid: ticketData.uuid,
        cita_id: citaId,
        cliente_nombre: ticketData.cliente_nombre || '',
        concepto: ticketData.producto || 'Cupón Zentura',
        importe: ticketData.precio || 0
      });

      const response = await fetch(`${baseUrl}cupones-externos/canjear`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reqBody)
      });

      const result = await response.json();
      setLoading(false);
      if (props && typeof props.showLoader === 'function') props.showLoader(false);

      if (result.error === 0) {
        // Borrar el contenido del input manual al canjear con éxito
        setCodigoInput('');

        // Notificar a la pestaña de Agenda para refrescar datos en tiempo real sin recargar página
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('agenda_refresh_needed', { detail: { timestamp: Date.now() } }));
          try {
            const channel = new BroadcastChannel('agenda_sync_channel');
            channel.postMessage({ type: 'REFRESH_AGENDA', timestamp: Date.now() });
            channel.close();
          } catch (e) {
            // BroadcastChannel no soportado en navegadores antiguos
          }
        }

        setModalCitasOpen(false);
        setModalDetalleOpen(false);

        Swal.fire({
          icon: 'success',
          title: '¡Cupón Canjeado!',
          confirmButtonColor: '#10B981',
          timer: 1500
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error al Canjear',
          text: result.errorMessage || 'No se pudo canjear el cupón.',
          confirmButtonColor: '#10B981'
        });
      }
    } catch (error) {
      setLoading(false);
      if (props && typeof props.showLoader === 'function') props.showLoader(false);
      console.error('Error al canjear cupón:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error de Conexión',
        text: 'Ocurrió un error de red al intentar canjear el cupón.',
        confirmButtonColor: '#10B981'
      });
    }
  };

  return (
    <Page title="Lector de Cupones">
      <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 800, margin: '0 auto' }}>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'bold', color: '#1E293B' }}>
          🎟️ Lector de Cupones
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: '#64748B' }}>
          Escanea el código QR del cliente o introduce el código manualmente para verificar y canjear cupones de Zentura.
        </Typography>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title="Escanear o Validar Cupón"
            subheader="Selecciona el método de entrada preferido"
            sx={{ bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', py: 2 }}
          />

          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            {/* SECCIÓN CÁMARA QR */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              {cameraActive ? (
                <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                  <Box
                    id="qr-reader-container"
                    ref={scannerRef}
                    sx={{
                      width: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid #10B981'
                    }}
                  />
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => setCameraActive(false)}
                    sx={{ mt: 2 }}
                    startIcon={<Iconify icon="eva:stop-circle-outline" />}
                  >
                    Apagar Cámara
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={() => setCameraActive(true)}
                  startIcon={<Iconify icon="eva:camera-fill" />}
                  sx={{ py: 1.5, px: 3, fontWeight: 'bold', fontSize: '15px' }}
                >
                  Encender Cámara para Escanear QR
                </Button>
              )}
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                O ENTRADA MANUAL
              </Typography>
            </Divider>

            {/* SECCIÓN ENTRADA MANUAL */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
              <TextField
                label="Código o UUID del Cupón"
                placeholder="Ej: 7A35E4D2X"
                value={codigoInput}
                onChange={(e) => setCodigoInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') validarCodigo();
                }}
                fullWidth
                InputProps={{
                  endAdornment: codigoInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="borrar código"
                        onClick={() => setCodigoInput('')}
                        edge="end"
                        size="small"
                      >
                        <Iconify icon="eva:close-circle-fill" width={22} height={22} sx={{ color: 'text.secondary' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  maxWidth: '450px',
                  '& .MuiOutlinedInput-root': {
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiInputBase-input': {
                    textAlign: 'left',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    paddingTop: '16px',
                    paddingBottom: '16px'
                  }
                }}
              />

              <Button
                variant="contained"
                color="success"
                size="large"
                disabled={loading}
                onClick={() => validarCodigo()}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="eva:checkmark-circle-2-fill" />}
                sx={{
                  height: '56px',
                  px: 4,
                  fontWeight: 'bold',
                  fontSize: '16px',
                  minWidth: '160px',
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {loading ? 'Validando...' : 'Validar Cupón'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Box>

      {/* MODAL 1: DETALLE DEL TICKET */}
      <Modal
        open={modalDetalleOpen}
        onClose={() => setModalDetalleOpen(false)}
        aria-labelledby="modal-ticket-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 550 },
            bgcolor: 'background.paper',
            borderRadius: 2.5,
            boxShadow: 24,
            p: { xs: 3, sm: 4 }
          }}
        >
          {ticketData && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography id="modal-ticket-title" variant="h5" sx={{ fontWeight: 'bold', color: '#0F172A' }}>
                  🎟️ Detalle del Cupón
                </Typography>
                <Chip
                  label={getEstadoLabel(ticketData)}
                  color={getEstadoColor(ticketData)}
                  sx={{ fontWeight: 'bold', fontSize: '13px', color: '#FFFFFF' }}
                />
              </Stack>

              <Divider sx={{ mb: 3 }} />

              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                        CÓDIGO / UUID:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1E293B' }}>
                        {ticketData.uuid || ticketData.codigo_corto || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                        CLIENTE:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#1E293B' }}>
                        {ticketData.cliente_nombre || 'No asignado'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                        PRODUCTO / SERVICIO:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                        {ticketData.producto || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                        PRECIO / IMPORTE:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        {ticketData.precio ? `${ticketData.precio} €` : 'N/A'}
                      </Typography>
                    </Grid>

                    {ticketData.descripcion && (
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
                          DESCRIPCIÓN:
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#475569' }}>
                          {ticketData.descripcion}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {ticketData.estado_uso === 'USADO' && (
                  <Paper sx={{ p: 2, bgcolor: '#FEF2F2', border: '1px solid #FCA5A5', textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ color: '#991B1B', fontWeight: 'bold' }}>
                      ⚠️ Este cupón ya fue canjeado anteriormente
                    </Typography>
                    {ticketData.fecha_uso && (
                      <Typography variant="caption" sx={{ color: '#7F1D1D' }}>
                        Fecha de uso: {ticketData.fecha_uso}
                      </Typography>
                    )}
                  </Paper>
                )}

                {ticketData.estado_uso !== 'USADO' && ticketData.caducado && (
                  <Paper sx={{ p: 2, bgcolor: '#FEF2F2', border: '1px solid #FCA5A5', textAlign: 'center' }}>
                    <Typography variant="subtitle2" sx={{ color: '#991B1B', fontWeight: 'bold' }}>
                      ⛔ Este cupón se encuentra caducado
                    </Typography>
                  </Paper>
                )}
              </Stack>

              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setModalDetalleOpen(false)}
                  sx={{ fontWeight: 'bold' }}
                >
                  Cerrar
                </Button>

                {ticketData.estado_uso !== 'USADO' && !ticketData.caducado && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                      setModalDetalleOpen(false);
                      handleAbrirCitas();
                    }}
                    startIcon={<Iconify icon="eva:calendar-outline" />}
                    sx={{ fontWeight: 'bold', px: 3 }}
                  >
                    Seleccionar Cita para Canjear
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Box>
      </Modal>

      {/* MODAL 2: LISTADO DE CITAS MARGEN +/- 3 HORAS */}
      <Modal
        open={modalCitasOpen}
        onClose={() => setModalCitasOpen(false)}
        aria-labelledby="modal-citas-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: 650 },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2.5,
            boxShadow: 24,
            p: { xs: 2.5, sm: 4 },
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography id="modal-citas-title" variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            📅 Seleccionar Cita (Agenda de Hoy)
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Elige la cita a la que se añadirá el código del cupón en su celda "COMMENT" y vinculará al cliente:
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            {(() => {
              if (loadingCitas) {
                return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                      Buscando citas disponibles...
                    </Typography>
                  </Box>
                );
              }
              if (citasRango.length === 0) {
                return (
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                    No se encontraron citas disponibles en la agenda para hoy.
                  </Typography>
                );
              }
              return (
                <Stack spacing={2}>
                  {citasRango.map((row) => {
                    let d = {};
                    try {
                      d = typeof row.datos === 'string' ? JSON.parse(row.datos) : (row.datos || {});
                    } catch (e) {
                      d = row.datos || {};
                    }

                    const horaStr = d.time || d.hora || 'Sin Hora';
                    const nombreClienteStr = d.name || d.nombre || d.cliente || 'Sin Nombre de Cliente';
                    const salaStr = d.room || d.sala;
                    const servicioStr = d.treatment || d.servicio;
                    const comentarioStr = d.comment || d.comentario;

                    return (
                      <Paper
                        key={row.id}
                        elevation={2}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#F8FAFC',
                          '&:hover': { borderColor: '#2563EB', backgroundColor: '#EFF6FF' }
                        }}
                      >
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} sm={8}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Chip
                                label={horaStr}
                                color={horaStr === 'Sin Hora' ? 'info' : 'primary'}
                                size="small"
                                sx={{ fontWeight: 'bold', fontSize: '12px' }}
                              />
                              {salaStr && (
                                <Chip
                                  label={`Sala: ${salaStr}`}
                                  variant="outlined"
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: '11px' }}
                                />
                              )}
                              {servicioStr && (
                                <Chip
                                  label={servicioStr}
                                  variant="outlined"
                                  color="secondary"
                                  size="small"
                                  sx={{ fontWeight: 600, fontSize: '11px' }}
                                />
                              )}
                            </Stack>

                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#0F172A' }}>
                              {nombreClienteStr}
                            </Typography>

                            {comentarioStr && (
                              <Typography variant="caption" sx={{ color: '#475569', fontStyle: 'italic', display: 'block' }}>
                                Nota: {comentarioStr}
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' }, mt: { xs: 1, sm: 0 } }}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => handleCanjearYCitar(row.id)}
                              startIcon={<Iconify icon="eva:plus-circle-fill" />}
                              sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
                            >
                              Asociar a esta Cita
                            </Button>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Stack>
              );
            })()}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setModalCitasOpen(false)}
              sx={{ fontWeight: 'bold' }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      </Modal>
    </Page>
  );
}
